import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { parsePhoneNumber } from 'awesome-phonenumber';
import styled from 'styled-components';
import { match, P } from 'ts-pattern';
import * as zod from 'zod';
import { Contact } from '@lgg/isomorphic/types/__generated__/graphql';
import { ContactLabel } from 'src/components/domain/contacts/contact-label';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import { Tooltip } from 'src/components/general/display/tooltip';
import { ConfirmationModalContent } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { LggModal } from 'src/components/general/modals/lgg-modal';
import { FlexRow } from 'src/components/layout/flex-row';
import { useFormatLastInteractionDate } from 'src/components/pages/conversations/hooks/use-format-last-interaction-date';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentUser } from 'src/hooks/use-current-user';
import { useUrls } from 'src/hooks/use-urls';
import { useVisible } from 'src/hooks/use-visible';

const conflictFieldSchema = zod.object({
  type: zod.enum(['EMAIL', 'PHONE']),
  value: zod.string(),
});

export const possibleDuplicateSchema = zod.object({
  id: zod.number(),
  label: zod.string(),
  conflictField: conflictFieldSchema,
  lastInteractionAt: zod.string().nullable(),
  assignedTo: zod.object({ id: zod.number(), name: zod.string() }).nullable(),
});

export type PossibleDuplicate = zod.infer<typeof possibleDuplicateSchema>;

const StrongText = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 22px;
`;

const FallbackContactLabel = ({ label }: { label: string }) => (
  <StrongText>{label}</StrongText>
);

const PossibleDuplicateContactModalFooter = styled(FlexRow)`
  justify-content: space-between;
  width: 100%;
`;

const ManagerOptionsContainer = styled(FlexRow)`
  gap: 15px;
  align-items: center;
`;

const AddAnywayOption = styled.span<{ $disabled?: boolean }>`
  color: ${({ theme }) => theme.colors.gogo};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 15px;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  text-align: center;
`;

const StyledContactLabel = styled(ContactLabel)`
  width: max-content;
  max-width: 200px;

  .contact-stage-icon_container {
    width: 18px;
    height: 18px;
    margin-right: 5px;

    .contact-stage-icon_icon {
      svg {
        width: 10.5px;
        height: 10.5px;
      }
    }
  }

  .contact-label-text-column {
    color: ${({ theme }) => theme.colors.smalt};
    font-family: ${({ theme }) => theme.font.medium};
    font-size: 13px;
    line-height: 16px;
    width: max-content;

    a {
      width: max-content;
    }
  }
`;

const MessageContainer = styled(FlexRow)`
  align-items: center;
  flex-wrap: wrap;
  line-height: 22px;
  margin-bottom: 10px;
  white-space: break-spaces;
`;

const TextBlockContainer = styled(FlexRow)`
  flex-wrap: wrap;
`;

type PossibleDuplicateContactModalContentProps = {
  duplicateData: zod.infer<typeof possibleDuplicateSchema>;
  contact: Contact | undefined;
  canManageContact: boolean;
  lastInteractionDateFormatter: (date: string) => string;
  footerContent: (props: { onClose }) => React.ReactElement;
  onClose: () => void;
};

const PossibleDuplicateContactModalContent = ({
  duplicateData,
  canManageContact,
  contact,
  lastInteractionDateFormatter,
  footerContent,
  onClose,
}: PossibleDuplicateContactModalContentProps) => {
  const { t } = useTranslation(['common', 'contacts']);

  const isEmailConflictField = duplicateData.conflictField.type === 'EMAIL';

  const fieldLabel = (
    isEmailConflictField ? t('common:email') : t('common:phoneNumber')
  ).toLowerCase();

  const fieldValue = isEmailConflictField
    ? duplicateData.conflictField.value
    : parsePhoneNumber(duplicateData.conflictField.value).number?.national ??
      duplicateData.conflictField.value;

  const message = (
    <Trans
      i18nKey="contacts:possibleDuplicateContactModal.message"
      values={{ fieldLabel, conflictFieldValue: fieldValue }}
      components={{
        strong: <StrongText />,
        text: <span />,
        contactLabel: contact ? (
          <StyledContactLabel
            contact={contact}
            disableInteractions={!canManageContact}
            showStageIcon
          />
        ) : (
          <FallbackContactLabel label={duplicateData.label} />
        ),
      }}
    />
  );

  // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
  const descriptionKey = match(duplicateData)
    .with(
      { assignedTo: P.not(P.nullish), lastInteractionAt: P.not(P.nullish) },
      () =>
        'contacts:possibleDuplicateContactModal.description.assignedWithInteraction' as const,
    )
    .with(
      { assignedTo: P.nullish, lastInteractionAt: P.nullish },
      () =>
        'contacts:possibleDuplicateContactModal.description.unassignedWithoutInteraction' as const,
    )
    .with(
      { lastInteractionAt: P.not(P.nullish) },
      () =>
        'contacts:possibleDuplicateContactModal.description.unassignedWithInteraction' as const,
    )
    .with(
      { assignedTo: P.not(P.nullish) },
      () =>
        'contacts:possibleDuplicateContactModal.description.assignedWithoutInteraction' as const,
    )
    .exhaustive();

  const description = (
    <Trans
      i18nKey={descriptionKey}
      values={{
        assigneeName: duplicateData.assignedTo?.name,
        lastInteractionDate: duplicateData.lastInteractionAt
          ? lastInteractionDateFormatter(duplicateData.lastInteractionAt)
          : '',
      }}
      components={{ strong: <StrongText />, text: <></> }}
    />
  );

  return (
    <ConfirmationModalContent
      title={t('contacts:possibleDuplicateContactModal.title')}
      message={
        <MessageContainer>
          <TextBlockContainer>{message}</TextBlockContainer>
          <span style={{ paddingTop: '18px', lineHeight: '18px' }}>{description}</span>
        </MessageContainer>
      }
      testId="possible-duplicate-contact-confirm-message"
      onClose={onClose}
      footerContent={footerContent}
      maxWidth={420}
      width="100%"
    />
  );
};

export const invalidPhoneNumberSchema = zod.object({
  property: zod.string(),
  value: zod.string(),
  constraints: zod.object({
    failed: zod.string(),
  }),
});

type PossibleDuplicateContactModalProps = {
  duplicateData: zod.infer<typeof possibleDuplicateSchema>;
  contact: Contact | undefined;
  onAddAnyway: () => void;
  onClose: () => void;
};

export const PossibleDuplicateContactModal = ({
  duplicateData,
  contact,
  onAddAnyway,
  onClose,
}: PossibleDuplicateContactModalProps) => {
  const visibilityHandler = useVisible(true);
  const { t } = useTranslation(['common', 'contacts']);
  const { getRelativeDate } = useFormatLastInteractionDate();
  const { getContactModalUrl } = useUrls();
  const duplicateContactUrl = getContactModalUrl({
    contactId: duplicateData.id,
  });

  const history = useHistory();
  const currentUser = useCurrentUser();
  const { hasPermission } = useAuthorization();
  const handleClose = () => {
    onClose();
    visibilityHandler.close();
  };
  const isManager = hasPermission('platform:company.contact:update:all');

  const canManageContact = isManager || duplicateData.assignedTo?.id === currentUser?.id;

  return (
    <LggModal
      visible={visibilityHandler.visible}
      closeIcon={<></>}
      onClose={handleClose}
      width="max-content"
    >
      <PossibleDuplicateContactModalContent
        onClose={handleClose}
        duplicateData={duplicateData}
        contact={contact}
        lastInteractionDateFormatter={getRelativeDate}
        canManageContact={canManageContact}
        footerContent={() => (
          <PossibleDuplicateContactModalFooter>
            <ButtonV2
              data-lgg-id="confirmation-modal-cancel-button"
              variant="default"
              size="small"
              onClick={handleClose}
            >
              {t('common:cancel')}
            </ButtonV2>
            <ManagerOptionsContainer>
              {canManageContact ? (
                <>
                  <AddAnywayOption
                    data-lgg-id="confirmation-modal-add-anyway-button"
                    onClick={onAddAnyway}
                  >
                    {t('contacts:possibleDuplicateContactModal.addAnyway')}
                  </AddAnywayOption>

                  <ButtonV2
                    variant="primary"
                    size="small"
                    onClick={() => {
                      history.push(duplicateContactUrl);
                    }}
                  >
                    {t('contacts:possibleDuplicateContactModal.viewContact')}
                  </ButtonV2>
                </>
              ) : (
                <>
                  <Tooltip
                    title={t(
                      'contacts:possibleDuplicateContactModal.addAnywayTooltipText',
                    )}
                    trigger="hover"
                    overlayInnerStyle={{
                      width: '120px',
                      background: 'rgba(64, 83, 100, 1)',
                    }}
                  >
                    <span>
                      <ButtonV2
                        variant="default"
                        size="small"
                        disabled
                        data-lgg-id="confirmation-modal-add-anyway-button"
                      >
                        {t('contacts:possibleDuplicateContactModal.addAnyway')}
                      </ButtonV2>
                    </span>
                  </Tooltip>
                </>
              )}
            </ManagerOptionsContainer>
          </PossibleDuplicateContactModalFooter>
        )}
      />
    </LggModal>
  );
};
